/**
 * E11 web-portal design spec — the webhook contract form. Unlike
 * GenerateJob.tsx/ConnectorJob.tsx, there is no document to drop
 * (design spec §1: WebhookSurface is the customer's own declared
 * contract, not extracted from a standard) — this page builds the
 * request directly, the first portal page that has to construct a
 * CanonicalSchema from nothing rather than upload or edit one.
 *
 * A new page at a new route, not a mode on ConnectorJob.tsx (design
 * spec §1): the input shape here is structurally incompatible with a
 * file-drop box, the same reasoning ConnectorJob.tsx's own docstring
 * already gives for why *it* isn't a mode on GenerateJob.tsx.
 */

import { useState } from 'react';

import {
  ApiError,
  generateWebhook,
  type CanonicalField,
  type GeneratedConnector,
  type SignatureScheme,
  type WebhookSurface,
} from '../api/client';
import { GeneratedFilesResult } from '../components/GeneratedFilesResult';
import { SchemaFieldListBuilder } from '../components/SchemaFieldListBuilder';

type SignatureKind = SignatureScheme['kind'];

interface WebhookEventDraft {
  eventType: string;
  payloadFields: CanonicalField[];
  hasTargetSchema: boolean;
  targetFields: CanonicalField[];
  idempotencyKeyPath: string;
}

function emptyField(): CanonicalField {
  return { name: '', type: 'string', nullable: false };
}

function newEvent(): WebhookEventDraft {
  return {
    eventType: '',
    payloadFields: [emptyField()],
    hasTargetSchema: false,
    targetFields: [emptyField()],
    idempotencyKeyPath: '',
  };
}

function namedFields(fields: CanonicalField[]): CanonicalField[] {
  return fields.filter((f) => f.name.trim() !== '');
}

export function WebhookJob() {
  const [name, setName] = useState('');
  const [signatureKind, setSignatureKind] = useState<SignatureKind>('none');
  const [headerName, setHeaderName] = useState('');
  const [signedPayloadTemplate, setSignedPayloadTemplate] = useState('');
  const [timestampHeaderName, setTimestampHeaderName] = useState('');
  const [eventTypeFieldPath, setEventTypeFieldPath] = useState('');
  const [dlqDeclared, setDlqDeclared] = useState(false);
  const [events, setEvents] = useState<WebhookEventDraft[]>([newEvent()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<GeneratedConnector | null>(null);

  const needsSignatureFields = signatureKind !== 'none';

  // Design spec §4 — a shallow "is there obviously something to
  // submit" gate, not a re-implementation of WebhookSurface's own
  // Pydantic validation. A genuinely malformed submission still
  // reaches the backend's existing 422.
  const canSubmit = events.every(
    (event) => event.eventType.trim() !== '' && namedFields(event.payloadFields).length > 0,
  );

  function updateEvent(index: number, patch: Partial<WebhookEventDraft>) {
    setEvents((prev) => prev.map((event, i) => (i === index ? { ...event, ...patch } : event)));
  }

  function addEvent() {
    setEvents((prev) => [...prev, newEvent()]);
  }

  function removeEvent(index: number) {
    setEvents((prev) => prev.filter((_, i) => i !== index));
  }

  async function generate() {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const surface: WebhookSurface = {
        name: name || 'webhook',
        source_format: 'webhook_declaration',
        signature: {
          kind: signatureKind,
          ...(needsSignatureFields
            ? {
                header_name: headerName || undefined,
                signed_payload_template: signedPayloadTemplate || undefined,
                timestamp_header_name: timestampHeaderName || undefined,
              }
            : {}),
        },
        event_type_field_path: eventTypeFieldPath,
        dlq_declared: dlqDeclared,
        events: events.map((event) => ({
          event_type: event.eventType,
          payload_schema: {
            name: `${event.eventType}_payload`,
            source_format: 'webhook_declaration',
            fields: namedFields(event.payloadFields),
          },
          ...(event.hasTargetSchema
            ? {
                target_schema: {
                  name: `${event.eventType}_target`,
                  source_format: 'webhook_declaration',
                  fields: namedFields(event.targetFields),
                },
              }
            : {}),
          ...(event.idempotencyKeyPath
            ? { idempotency_key_path: event.idempotencyKeyPath }
            : {}),
        })),
      };
      const generated = await generateWebhook(surface);
      setResult(generated);
    } catch (err) {
      setError(err instanceof ApiError ? (err.reason ?? err.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <h1>Generate a webhook handler</h1>
      <p className="muted">
        Declare your webhook contract below. Nothing you enter is stored — the generated handler
        files are the only thing returned.
      </p>

      <label className="field-inline">
        <span>Contract name</span>
        <input
          type="text"
          placeholder="My webhook integration"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <fieldset>
        <legend>Signature scheme</legend>
        <label className="field-inline">
          <span>Kind</span>
          <select
            value={signatureKind}
            onChange={(event) => setSignatureKind(event.target.value as SignatureKind)}
          >
            <option value="none">None</option>
            <option value="hmac_sha256_hex">HMAC-SHA256 (hex)</option>
            <option value="hmac_sha256_base64">HMAC-SHA256 (base64)</option>
            <option value="hmac_sha1_hex">HMAC-SHA1 (hex)</option>
          </select>
        </label>

        {needsSignatureFields && (
          <>
            <label className="field-inline">
              <span>Signature header name</span>
              <input
                type="text"
                placeholder="X-Hub-Signature-256"
                value={headerName}
                onChange={(event) => setHeaderName(event.target.value)}
              />
            </label>
            <label className="field-inline">
              <span>Signed payload template</span>
              <input
                type="text"
                placeholder="{timestamp}.{body}"
                value={signedPayloadTemplate}
                onChange={(event) => setSignedPayloadTemplate(event.target.value)}
              />
            </label>
            <label className="field-inline">
              <span>Timestamp header name</span>
              <input
                type="text"
                placeholder="(only if the template above references {timestamp})"
                value={timestampHeaderName}
                onChange={(event) => setTimestampHeaderName(event.target.value)}
              />
            </label>
          </>
        )}
      </fieldset>

      <label className="field-inline">
        <span>Event type field path</span>
        <input
          type="text"
          placeholder="type"
          value={eventTypeFieldPath}
          onChange={(event) => setEventTypeFieldPath(event.target.value)}
        />
      </label>

      <label className="field-inline">
        <span>Dead-letter queue</span>
        <input
          type="checkbox"
          checked={dlqDeclared}
          onChange={(event) => setDlqDeclared(event.target.checked)}
          aria-label="Dead-letter queue declared"
        />
      </label>

      <h2>Events</h2>
      {events.map((event, i) => (
        <fieldset key={i} className="webhook-event">
          <legend>Event {i + 1}</legend>

          <label className="field-inline">
            <span>Event type</span>
            <input
              type="text"
              placeholder="payment_intent.succeeded"
              value={event.eventType}
              onChange={(e) => updateEvent(i, { eventType: e.target.value })}
              aria-label={`Event ${i + 1} type`}
            />
          </label>

          <label className="field-inline">
            <span>Idempotency key path (optional)</span>
            <input
              type="text"
              placeholder="id"
              value={event.idempotencyKeyPath}
              onChange={(e) => updateEvent(i, { idempotencyKeyPath: e.target.value })}
              aria-label={`Event ${i + 1} idempotency key path`}
            />
          </label>

          <SchemaFieldListBuilder
            label={`Event ${i + 1} payload`}
            fields={event.payloadFields}
            onChange={(fields) => updateEvent(i, { payloadFields: fields })}
          />

          <label className="field-inline">
            <span>Map to a target schema</span>
            <input
              type="checkbox"
              checked={event.hasTargetSchema}
              onChange={(e) => updateEvent(i, { hasTargetSchema: e.target.checked })}
              aria-label={`Event ${i + 1} has a target schema`}
            />
          </label>

          {event.hasTargetSchema && (
            <SchemaFieldListBuilder
              label={`Event ${i + 1} target`}
              fields={event.targetFields}
              onChange={(fields) => updateEvent(i, { targetFields: fields })}
            />
          )}

          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => removeEvent(i)}
            disabled={events.length <= 1}
          >
            Remove event {i + 1}
          </button>
        </fieldset>
      ))}
      <button type="button" className="btn btn--ghost" onClick={addEvent}>
        Add event
      </button>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <button
        className="btn btn--primary"
        disabled={!canSubmit || busy}
        onClick={() => void generate()}
      >
        {busy ? 'Generating…' : 'Generate webhook handler'}
      </button>

      {result && <GeneratedFilesResult result={result} />}
    </main>
  );
}
